import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { DataGrid, GridColDef, GridColumnVisibilityModel, GridExpandMoreIcon } from '@mui/x-data-grid';

import CopyableField from '~/components/Common/CopyableField';
import GridPagination from '~/components/Common/Elements/GridPagination';
import PageSubheader from '~/components/Common/Elements/PageSubheader';
import DebugModal from '~/components/DebugModal/DebugModal';
import PromoRequestItems from '~/components/Promo/PromoRequests/PromoRequestItems';
import PromoRequestVoucherLogs from '~/components/Promo/PromoRequests/PromoRequestVoucherLogs';
import { type DisplayMode } from '~/components/Promo/PromoRequests/displayMode';
import ColumnSelector from '~/components/Promo/PromoTester/ColumnSelector';
import PromoCodeUser from '~/components/Promo/formatters/PromoCodeUser';
import usePromoMeta from '~/components/Promo/hooks/usePromoMeta';
import { promoCodePath } from '~/components/Promo/promoUrls';

import { DEFAULT_PAGE_SIZES } from '~/consts/filters';

import { PromoMeta, PromoRequest } from '~/services/PromoService';

import dateFormatterDetailed from '~/utils/dateFormatterDetails';
import statusCodeFormatter from '~/utils/statusCodeFormatter';

import usePromoRequests from '../Promo/hooks/usePromoRequests';

type Props = {
  voucherName: string;
  userId: string;
  brand: string;
  /** When the parent already loaded promo meta, pass it so this section uses the same object (avoids a second `usePromoMeta` that can lag behind the page’s debug / other pickers). */
  promoMeta?: PromoMeta | null;
  /** Prefix for DOM ids when multiple request tables are rendered on one page. */
  idPrefix?: string;
  /** Item fields to make visible by default in request/log items. */
  defaultVisibleItemFields?: Array<string>;
  /**
   * When false, omits the voucher logs accordion (e.g. parent shows `VoucherLogsTable` with the same data).
   * @default true
   */
  showVoucherLogsAccordion?: boolean;
  /** Opens the request section when reached from a recent-activity deep link. */
  focusRecentRequests?: boolean;
  /** Controls contextual columns in the Recent Requests table. */
  displayMode?: DisplayMode;
};

type UserIdRequestFilterProps = {
  inputId: string;
  userIdInput: string;
  setUserIdInput: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onClear: () => void;
};

function UserIdRequestFilter({ inputId, userIdInput, setUserIdInput, onSubmit, onClear }: UserIdRequestFilterProps) {
  return (
    <Box component="form" onSubmit={onSubmit} sx={{ mb: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center">
        <TextField
          id={inputId}
          label="User ID"
          size="small"
          value={userIdInput}
          onChange={(e) => setUserIdInput(e.target.value)}
          sx={{ minWidth: 300 }}
        />
        <Button variant="contained" color="primary" type="submit">
          Search
        </Button>
        {userIdInput ? (
          <Button variant="text" type="button" onClick={onClear}>
            Clear
          </Button>
        ) : null}
      </Stack>
    </Box>
  );
}

const sharedOpts: Partial<GridColDef> = {
  sortable: false,
  display: 'flex',
  flex: 0,
  disableColumnMenu: true,
};

function renderVoucherCopyableLink({ voucherId, voucherName }: { voucherId?: string; voucherName: string }) {
  return (
    <CopyableField
      key={`${voucherId ?? 'name'}-${voucherName}`}
      value={voucherName}
      label={
        voucherId ? (
          <Link href={`/marketing/vouchers/${voucherId}`} target="_blank" rel="noreferrer" color="inherit">
            {voucherName}
          </Link>
        ) : (
          voucherName
        )
      }
    />
  );
}

function getColumns(displayMode: DisplayMode): Array<GridColDef<PromoRequest & { uniqueKey: string }>> {
  return [
    {
      field: 'requestAt',
      headerName: 'Request At',
      renderCell: (params) => dateFormatterDetailed({ date: params.row.createdAt, includeTime: true }),
      ...sharedOpts,
      flex: 1,
    },
    ...(displayMode === 'voucher'
      ? []
      : [
          {
            field: 'codeName',
            headerName: 'Code Name',
            renderCell: (params) => (
              <CopyableField
                value={params.row.codeName}
                label={
                  <Link href={promoCodePath(params.row.codeName)} target="_blank" rel="noreferrer" color="inherit">
                    {params.row.codeName}
                  </Link>
                }
              />
            ),
            ...sharedOpts,
            flex: 1,
          },
        ]),
    {
      field: 'requestedBy',
      headerName: 'Requested By',
      renderCell: (params) => <PromoCodeUser userId={params.row.userId} userIdFor={params.row.userIdFor} />,
      ...sharedOpts,
      flex: 1,
    },
    {
      field: 'voucherName',
      headerName: 'Voucher',
      renderCell: (params) => {
        const voucherIds = params.row.bodyJSON?.voucherIds ?? [];
        const voucherNames = params.row.voucherNames ?? [];
        const linkedVoucherNames = voucherNames.map((name: string, index: number) => ({
          voucherName: name,
          voucherId: voucherIds[index],
        }));
        const voucherIdsWithoutNames = voucherIds.slice(voucherNames.length);

        return (
          <Stack>
            {linkedVoucherNames.map(({ voucherId, voucherName }) =>
              renderVoucherCopyableLink({ voucherId, voucherName }),
            )}
            {voucherIdsWithoutNames.map((voucherId: string) =>
              renderVoucherCopyableLink({ voucherId, voucherName: voucherId }),
            )}
          </Stack>
        );
      },
      ...sharedOpts,
      flex: 1,
    },
    {
      field: 'statusCode',
      headerName: 'Status',
      renderCell: (params) =>
        statusCodeFormatter({ extended: true }, params.row.statusCode, params.row.errorCode, params.row.errorMsg),
      ...sharedOpts,
      flex: 2,
    },
    {
      field: 'region',
      headerName: 'Region',
      renderCell: (params) => params.row.bodyJSON?.order?.region ?? '-',
      ...sharedOpts,
    },
    {
      field: 'debug',
      headerName: '',
      renderCell: (params) => <DebugModal title="Request Debug" type="generic" data={params.row ?? {}} />,
      ...sharedOpts,
      flex: 0,
    },
  ];
}

export default function VoucherRecentRequests({
  voucherName,
  userId: propUserId,
  brand,
  promoMeta: promoMetaProp,
  idPrefix,
  defaultVisibleItemFields = [],
  showVoucherLogsAccordion = true,
  focusRecentRequests = false,
  displayMode = 'all',
}: Props) {
  const fallbackIdPrefix = useId();
  const resolvedIdPrefix = idPrefix ?? fallbackIdPrefix;
  const [expanded, setExpanded] = useState(false);
  const [itemsExpanded, setItemsExpanded] = useState(false);
  const [voucherLogsExpanded, setVoucherLogsExpanded] = useState(false);
  const [userIdInput, setUserIdInput] = useState(propUserId || '');

  useEffect(() => {
    if (focusRecentRequests) {
      setExpanded(true);
    }
  }, [focusRecentRequests]);

  const { promoMeta: promoMetaFromHook } = usePromoMeta();
  const promoMeta = promoMetaProp !== undefined ? promoMetaProp : promoMetaFromHook;

  const { promoRequests, promoRequestItems, totalRows, isLoading, promoRequestsFilter, setPromoRequestsFilter } =
    usePromoRequests({
      initVoucherName: voucherName || undefined,
      initUserId: propUserId || undefined,
      initLimit: 10,
      brand,
    });

  useEffect(() => {
    setPromoRequestsFilter((prev) => ({
      ...prev,
      voucherName: voucherName || undefined,
      userId: propUserId || undefined,
      page: 0,
    }));
    setUserIdInput(propUserId || '');
  }, [voucherName, propUserId, setPromoRequestsFilter]);

  const handleFilterByUserId = useCallback(
    (userId: string) => {
      setUserIdInput(userId);
      setPromoRequestsFilter((prev) => ({
        ...prev,
        userId: userId || undefined,
        page: 0,
      }));
    },
    [setPromoRequestsFilter],
  );

  const handleSearchSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setPromoRequestsFilter((prev) => ({
        ...prev,
        userId: userIdInput || undefined,
        page: 0,
      }));
    },
    [userIdInput, setPromoRequestsFilter],
  );

  const handleClearUserId = useCallback(() => {
    setUserIdInput('');
    setPromoRequestsFilter((prev) => ({
      ...prev,
      userId: undefined,
      page: 0,
    }));
  }, [setPromoRequestsFilter]);

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({});

  const handleColumnVisibilityChange = useCallback((field: string, visible: boolean) => {
    setColumnVisibilityModel((prev) => ({ ...prev, [field]: visible }));
  }, []);

  const columns = useMemo(() => getColumns(displayMode), [displayMode]);

  const requestSourceSelectOptions = useMemo(() => {
    const raw = promoMeta?.request_source_options ?? [];
    const forAdmin = raw.filter((o) => o.showInAdmin);
    return (forAdmin.length > 0 ? forAdmin : raw).map((o) => o.key).sort((a, b) => a.localeCompare(b));
  }, [promoMeta]);

  const showRequestSourceFilter = requestSourceSelectOptions.length > 0;

  const onPageChange = useCallback(
    (page: number, pageSize: number) => {
      setPromoRequestsFilter((prev) => ({
        ...prev,
        page,
        limit: pageSize,
      }));
    },
    [setPromoRequestsFilter],
  );

  return (
    <Box mt={3}>
      <UserIdRequestFilter
        inputId={`${resolvedIdPrefix}-voucher-promo-requests-user-id`}
        userIdInput={userIdInput}
        setUserIdInput={setUserIdInput}
        onSubmit={handleSearchSubmit}
        onClear={handleClearUserId}
      />

      <Accordion disableGutters expanded={expanded} onChange={(_, isExpanded) => setExpanded(isExpanded)}>
        <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
          <PageSubheader title={`Recent Requests (${totalRows})`} />
        </AccordionSummary>
        <AccordionDetails sx={{ overflow: 'visible' }}>
          <Stack
            direction="row"
            flexWrap="wrap"
            alignItems="center"
            justifyContent={showRequestSourceFilter ? 'space-between' : 'flex-end'}
            gap={2}
            sx={{ mb: 2 }}
          >
            {showRequestSourceFilter ? (
              <FormControl size="small" sx={{ minWidth: 220, maxWidth: '100%' }} variant="outlined">
                <InputLabel id={`${resolvedIdPrefix}-voucher-recent-requests-request-source-label`}>
                  Request source
                </InputLabel>
                <Select
                  labelId={`${resolvedIdPrefix}-voucher-recent-requests-request-source-label`}
                  id={`${resolvedIdPrefix}-voucher-recent-requests-request-source`}
                  value={promoRequestsFilter.requestSource ?? ''}
                  label="Request source"
                  onChange={(e) => {
                    const v = e.target.value;
                    setPromoRequestsFilter((prev) => ({
                      ...prev,
                      requestSource: v || undefined,
                      page: 0,
                    }));
                  }}
                >
                  <MenuItem value="">
                    <em>All</em>
                  </MenuItem>
                  {requestSourceSelectOptions.map((key) => (
                    <MenuItem key={key} value={key}>
                      {key}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null}
            <ColumnSelector
              columns={columns}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityChange={handleColumnVisibilityChange}
            />
          </Stack>
          <Box>
            <DataGrid
              columns={columns}
              autoHeight
              getRowId={(row) => row.requestId}
              rowCount={totalRows}
              rows={promoRequests}
              pagination
              paginationMode="server"
              paginationModel={{
                page: promoRequestsFilter.page,
                pageSize: promoRequestsFilter.limit,
              }}
              slots={{ pagination: GridPagination }}
              pageSizeOptions={DEFAULT_PAGE_SIZES}
              onPaginationModelChange={({ page, pageSize }) => onPageChange(page, pageSize)}
              getRowHeight={() => 'auto'}
              loading={isLoading}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={setColumnVisibilityModel}
            />
          </Box>
        </AccordionDetails>
      </Accordion>

      <PromoRequestItems
        meta={promoMeta}
        promoRequestItems={promoRequestItems}
        highlightedRows={[]}
        isLoading={isLoading}
        requestType="Request Related"
        expanded={itemsExpanded}
        onExpandedChange={setItemsExpanded}
        promoRequestsFilter={promoRequestsFilter}
        setPromoRequestsFilter={setPromoRequestsFilter}
        setFormUserId={handleFilterByUserId}
        hidePromoCodeFields
        hideRequestSourceFilter
        defaultVisibleItemFields={defaultVisibleItemFields}
      />

      {showVoucherLogsAccordion ? (
        <PromoRequestVoucherLogs
          expanded={voucherLogsExpanded}
          onExpandedChange={setVoucherLogsExpanded}
          voucherNames={
            promoRequestsFilter.voucherName
              ? [promoRequestsFilter.voucherName]
              : voucherName
                ? [voucherName]
                : undefined
          }
          userId={promoRequestsFilter.userId}
          brand={promoRequestsFilter.brand}
          promoRequestsFilter={promoRequestsFilter}
          setPromoRequestsFilter={setPromoRequestsFilter}
          setFormUserId={handleFilterByUserId}
        />
      ) : null}
    </Box>
  );
}
